import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getRewardEngine,
  patchRewardEngine,
} from "services/apiMerchantRewardEngine";

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (e?.message) return e.message;
  return fallback;
}

const MerchantRewardEngine = () => {
  const [loading, setLoading] = useState(true);
  const [engine, setEngine] = useState(null);
  const [numbersToSelect, setNumbersToSelect] = useState("");
  const [numberRange, setNumberRange] = useState("");
  const [dailyPriceNgn, setDailyPriceNgn] = useState("");
  const [freeDays, setFreeDays] = useState("");
  const [maxWinDraw, setMaxWinDraw] = useState("");
  const [maxWinDay, setMaxWinDay] = useState("");
  const [megaJackpot, setMegaJackpot] = useState("");
  const [submissionsPaused, setSubmissionsPaused] = useState(false);
  const [probJson, setProbJson] = useState("");
  const [rewardTypesJson, setRewardTypesJson] = useState("");
  const [campaignsJson, setCampaignsJson] = useState("");
  const [inventoryJson, setInventoryJson] = useState("");
  const [fraudJson, setFraudJson] = useState("");

  const hydrate = (cfg) => {
    setEngine(cfg);
    setNumbersToSelect(String(cfg.numbersToSelect ?? ""));
    setNumberRange(String(cfg.numberRange ?? ""));
    setDailyPriceNgn(String(cfg.dailyPriceNgn ?? ""));
    setFreeDays(String(cfg.freeDays ?? ""));
    setMaxWinDraw(String(cfg.maxWinPerUserPerDraw ?? ""));
    setMaxWinDay(String(cfg.maxWinPerUserPerDay ?? ""));
    setMegaJackpot(String(cfg.megaJackpotDisplayNgn ?? ""));
    setSubmissionsPaused(!!cfg.submissionsPaused);
    setProbJson(JSON.stringify(cfg.probabilityWeights ?? {}, null, 2));
    setRewardTypesJson(JSON.stringify(cfg.rewardTypes ?? [], null, 2));
    setCampaignsJson(JSON.stringify(cfg.campaigns ?? [], null, 2));
    setInventoryJson(JSON.stringify(cfg.inventory ?? [], null, 2));
    setFraudJson(JSON.stringify(cfg.fraudMonitoring ?? {}, null, 2));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await getRewardEngine();
      hydrate(cfg);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load reward engine"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveCore = async (e) => {
    e.preventDefault();
    try {
      const body = { submissionsPaused };
      if (numbersToSelect !== "") body.numbersToSelect = parseInt(numbersToSelect, 10);
      if (numberRange !== "") body.numberRange = parseInt(numberRange, 10);
      if (dailyPriceNgn !== "") body.dailyPriceNgn = parseFloat(dailyPriceNgn);
      if (freeDays !== "") body.freeDays = parseInt(freeDays, 10);
      if (maxWinDraw !== "") body.maxWinPerUserPerDraw = parseFloat(maxWinDraw);
      if (maxWinDay !== "") body.maxWinPerUserPerDay = parseFloat(maxWinDay);
      if (megaJackpot !== "") body.megaJackpotDisplayNgn = parseFloat(megaJackpot);
      const updated = await patchRewardEngine(body);
      hydrate(updated);
      toast.success("Reward engine settings saved");
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };

  const saveJsonBlock = async (e, field, raw, label) => {
    e.preventDefault();
    try {
      const parsed = JSON.parse(raw);
      const body = { [field]: parsed };
      const updated = await patchRewardEngine(body);
      hydrate(updated);
      toast.success(`${label} updated`);
    } catch (e) {
      toast.error(errMsg(e, `Invalid JSON for ${label}`));
    }
  };

  if (loading && !engine) {
    return (
      <div className="p-6 text-center text-slate-600 dark:text-zink-200">
        Loading reward engine…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 dark:text-zink-100">
          Reward engine
        </h1>
        <p className="text-slate-600 dark:text-zink-300 text-sm mt-2">
          Control reward types, probability weights, jackpot thresholds, campaigns, inventory, and fraud
          rules for your service. Vouchers and revenue share remain under{" "}
          <Link to="/merchant-business" className="text-custom-500 underline">
            Business &amp; vouchers
          </Link>
          . Shadow mode is admin-only.
        </p>
        {engine && (
          <p className="text-xs text-slate-500 mt-2">
            Service <strong>{engine.serviceId}</strong> — config #{engine.ruleConfigId}
            {engine.shadowMode ? " — shadow mode ON (no real payouts)" : ""}
            {engine.active === false ? " — inactive" : ""}
          </p>
        )}
      </div>

      <section className="card p-6 shadow border border-slate-100 dark:border-zink-600 dark:bg-zink-800">
        <h2 className="text-lg font-medium mb-4">Lottery, caps &amp; submissions</h2>
        <form onSubmit={saveCore} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">Numbers to select</label>
            <input
              className="form-input w-full dark:bg-zink-700 dark:text-zink-100"
              value={numbersToSelect}
              onChange={(e) => setNumbersToSelect(e.target.value)}
              type="number"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Number range</label>
            <input
              className="form-input w-full dark:bg-zink-700 dark:text-zink-100"
              value={numberRange}
              onChange={(e) => setNumberRange(e.target.value)}
              type="number"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Daily price (NGN)</label>
            <input
              className="form-input w-full dark:bg-zink-700 dark:text-zink-100"
              value={dailyPriceNgn}
              onChange={(e) => setDailyPriceNgn(e.target.value)}
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Free days</label>
            <input
              className="form-input w-full dark:bg-zink-700 dark:text-zink-100"
              value={freeDays}
              onChange={(e) => setFreeDays(e.target.value)}
              type="number"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Max win per user per draw (NGN)</label>
            <input
              className="form-input w-full dark:bg-zink-700 dark:text-zink-100"
              value={maxWinDraw}
              onChange={(e) => setMaxWinDraw(e.target.value)}
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Max win per user per day (NGN)</label>
            <input
              className="form-input w-full dark:bg-zink-700 dark:text-zink-100"
              value={maxWinDay}
              onChange={(e) => setMaxWinDay(e.target.value)}
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Mega jackpot display (NGN)</label>
            <input
              className="form-input w-full dark:bg-zink-700 dark:text-zink-100"
              value={megaJackpot}
              onChange={(e) => setMegaJackpot(e.target.value)}
              type="number"
              step="0.01"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <input
              id="subPaused"
              type="checkbox"
              checked={submissionsPaused}
              onChange={(e) => setSubmissionsPaused(e.target.checked)}
            />
            <label htmlFor="subPaused" className="text-sm">
              Pause ticket submissions
            </label>
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="btn bg-custom-500 text-white px-4 py-2 rounded-md">
              Save core settings
            </button>
          </div>
        </form>
      </section>

      <JsonEditorSection
        title="Reward types"
        hint="Enable/disable reward categories and fulfillment channels."
        value={rewardTypesJson}
        onChange={setRewardTypesJson}
        onSave={(e) => saveJsonBlock(e, "rewardTypes", rewardTypesJson, "Reward types")}
      />
      <JsonEditorSection
        title="Probability engine"
        hint="Weights should sum to ~1.0 (e.g. no_win, small_win, medium_win, jackpot)."
        value={probJson}
        onChange={setProbJson}
        onSave={(e) => saveJsonBlock(e, "probabilityWeights", probJson, "Probability weights")}
      />
      <JsonEditorSection
        title="Campaign scheduling"
        value={campaignsJson}
        onChange={setCampaignsJson}
        onSave={(e) => saveJsonBlock(e, "campaigns", campaignsJson, "Campaigns")}
      />
      <JsonEditorSection
        title="Reward inventory"
        value={inventoryJson}
        onChange={setInventoryJson}
        onSave={(e) => saveJsonBlock(e, "inventory", inventoryJson, "Inventory")}
      />
      <JsonEditorSection
        title="Fraud monitoring"
        value={fraudJson}
        onChange={setFraudJson}
        onSave={(e) => saveJsonBlock(e, "fraudMonitoring", fraudJson, "Fraud monitoring")}
      />
    </div>
  );
};

function JsonEditorSection({ title, hint, value, onChange, onSave }) {
  return (
    <section className="card p-6 shadow border border-slate-100 dark:border-zink-600 dark:bg-zink-800">
      <h2 className="text-lg font-medium mb-1">{title}</h2>
      {hint && <p className="text-xs text-slate-500 mb-3">{hint}</p>}
      <form onSubmit={onSave}>
        <textarea
          className="form-input w-full font-mono text-xs min-h-[140px] dark:bg-zink-700 dark:text-zink-100"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
        />
        <button type="submit" className="btn bg-custom-500 text-white px-4 py-2 rounded-md mt-3">
          Save {title.toLowerCase()}
        </button>
      </form>
    </section>
  );
}

export default MerchantRewardEngine;
